import { useRecoilValue, useSetRecoilState } from "recoil";
import { Box, Flex, Text } from "rebass";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";

import { useUser } from "../../Hooks/useUser";
import { useProEditAccess } from "../../Hooks/useProEditAccess";
import { SidebarExpandedState } from "../../State/sidebar";
import { SidebarCollapsed } from "./SidebarCollapsed";
import { SidebarLoginWidget } from "./SidebarLoginWidget";
import { NAV_LINKS, isActive } from "./utils";
import {
  navLinkActive,
  navLinkActiveFill,
  sidebarContainerStyle,
  NavItem,
  NavLinkText,
  StyledBiCompass,
  StyledBiCompassFilled,
  StyledBiPalette,
  StyledBiUser,
  StyledBiUserFilled,
  StyledBiVideo,
  StyledBiEdit,
  StyledDivider,
  StyledHistoryLogo,
  StyledBiTrophy,
  StyledTeamsLogo,
  StyledTeamsLogoFilled,
} from "./styledComponents";
import { Avatar } from "../AvatarV2";
import { ModalType, TUserGroup, TUserRank } from "MVVM/@types";
import { AllstarModalState } from "../../State/modals";
import { Button } from "../Button";
import { usePortal } from "../../Hooks/usePortal";
import { PartnerTypeModal } from "../Modals/PartnerType";
import { TimelineEditorInfoModal } from "../Modals/TimelineEditorInfo";
import { CompetitionViewModel } from "../../Views/Competitions/ViewModel";
import { analytics } from "MVVM/Analytics";
import { AdUnit } from "../Ads";

const Sidebar = () => {
  const expanded = useRecoilValue(SidebarExpandedState);
  const { allstarUser } = useUser();
  const { hasProEditAccess } = useProEditAccess();
  const { portalOpen, setPortalOpen, Portal } = usePortal(<PartnerTypeModal />);
  const {
    portalOpen: timelinePortalOpen,
    setPortalOpen: setTimelinePortalOpen,
    Portal: TimelinePortal,
  } = usePortal(<TimelineEditorInfoModal />);
  const setAllstarModalState = useSetRecoilState(AllstarModalState);
  const { t } = useTranslation("translation", {
    keyPrefix: "nav",
  });
  const { data: competitionData } = CompetitionViewModel({
    activeOnly: true,
  });
  const competitionLink = competitionData?.competitions[0]?.slug;

  if (!expanded) return <SidebarCollapsed competitionLink={competitionLink} />;

  const username = allstarUser?.user?.profile?.username;
  const profileLink = username
    ? `${NAV_LINKS.profile}/${username}`
    : NAV_LINKS.login;

  const matchHistoryText = "Match History";

  return (
    <SidebarContainer>
      {portalOpen && Portal}
      {timelinePortalOpen && TimelinePortal}
      <Flex
        flexDirection="column"
        alignItems="flex-start"
        justifyContent="flex-start"
        style={{ gap: 8, flex: 1 }}
      >
        <NavItem>
          <NavLink to={NAV_LINKS.root} $isActive={isActive([NAV_LINKS.root])}>
            {isActive([NAV_LINKS.root]) ? (
              <StyledBiCompassFilled />
            ) : (
              <StyledBiCompass />
            )}
            <NavLinkText>{t("main.explore", "Explore")}</NavLinkText>
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            to={profileLink}
            $isActive={isActive(["/u", NAV_LINKS.profile])}
          >
            {isActive(["/u", NAV_LINKS.profile]) ? (
              <StyledBiUserFilled />
            ) : (
              <StyledBiUser />
            )}
            <NavLinkText>{t("main.myProfile", "My Profile")}</NavLinkText>
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            to={NAV_LINKS.teams}
            $isActive={isActive(["/teams", NAV_LINKS.teams, "/team"])}
            onClick={() => {
              analytics.track("Button - Click", {
                locationIdentifier: "teams",
                location: "left_nav",
              });
            }}
          >
            {isActive(["/teams", NAV_LINKS.teams, "/team"]) ? (
              <StyledTeamsLogoFilled />
            ) : (
              <StyledTeamsLogo />
            )}
            <NavLinkText>Teams</NavLinkText>
            <StyledNewBadge>New</StyledNewBadge>
          </NavLink>
        </NavItem>
        <SidebarLoginWidget />
        <StyledDivider />
        <NavSubheader>Create</NavSubheader>
        <NavItem>
          <NavLink
            to={NAV_LINKS.matchHistory}
            $isActive={isActive([NAV_LINKS.matchHistory])}
            $fill={true}
          >
            <StyledHistoryLogo />
            <NavLinkText>{matchHistoryText}</NavLinkText>
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink to={NAV_LINKS.studio} $isActive={false}>
            <StyledBiPalette />
            <NavLinkText>{t("main.studio", "Studio")}</NavLinkText>
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            to={NAV_LINKS.montages}
            $isActive={isActive([NAV_LINKS.montages])}
          >
            <StyledBiVideo />
            <NavLinkText>{t("main.montages", "Montages")}</NavLinkText>
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            to="#"
            onClick={() => {
              analytics.track("Button - Click", {
                locationIdentifier: "timeline_editor",
                location: "left_nav",
              });
              setTimelinePortalOpen(true);
            }}
            $isActive={false}
          >
            <StyledBiEdit />
            <NavLinkText>Timeline</NavLinkText>
            <StyledBetaBadge>Beta</StyledBetaBadge>
          </NavLink>
        </NavItem>
        <StyledDivider />
        {competitionLink && (
          <>
            <NavSubheader data-testid="earn">Earn</NavSubheader>
            <NavItem>
              <NavLink
                to={`/competitions/${competitionLink}`}
                $isActive={false}
                data-testid="competitionLink"
              >
                <StyledBiTrophy />
                <NavLinkText>
                  {t("main.competitions", "Competitions")}
                </NavLinkText>
              </NavLink>
            </NavItem>
            <StyledDivider />
          </>
        )}
        {allstarUser.loggedIn && (
          <NavItem>
            <NavLink
              to={NAV_LINKS.upgrade}
              $isActive={isActive([NAV_LINKS.upgrade])}
            >
              <NavLinkText>{t("main.upgrade", "Upgrade")}</NavLinkText>
            </NavLink>
          </NavItem>
        )}
        <NavItem>
          <NavLink to="/blog" $isActive={isActive(["/blog"])}>
            <NavLinkText>Dev Blog</NavLinkText>
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink to="#" onClick={() => setPortalOpen(true)} $isActive={false}>
            <NavLinkText>For Partners</NavLinkText>
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            to="#"
            onClick={(e) => {
              const userGroup = allstarUser?.user?.userGroup?.type;
              if (!allstarUser.loggedIn || !userGroup || userGroup === "FREE") {
                e.preventDefault();
                setAllstarModalState({
                  data: {},
                  functions: {},
                  isOpen: ModalType.DiscordSupport,
                });
              } else {
                window.location.replace(NAV_LINKS.support);
              }
            }}
            $isActive={false}
          >
            <NavLinkText>{t("main.support", "Support")}</NavLinkText>
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink to="/start" $isActive={isActive(["/start"])}>
            <NavLinkText>How Allstar Works</NavLinkText>
          </NavLink>
        </NavItem>
      </Flex>
    </SidebarContainer>
  );
};

const StyledBetaBadge = styled(Button)`
  background: linear-gradient(227.35deg, #72439a 6.85%, #f95370 89.04%);
  border-radius: 100px;
  color: ${({ theme }) => theme.colors.ice} !important;
  font-size: 10px;
  font-weight: 600;
  height: 20px;
  line-height: 12px;
  padding: 4px;
  text-transform: uppercase;
  width: 40px;
`;

const StyledNewBadge = styled(Button)`
  background: ${({ theme }) => theme.colors.envy};
  border-radius: 100px;
  color: ${({ theme }) => theme.colors.ice} !important;
  font-size: 10px;
  font-weight: 600;
  height: 20px;
  line-height: 12px;
  padding: 4px;
  text-transform: uppercase;
  width: 40px;
`;

const SidebarContainer = styled(Box)`
  ${sidebarContainerStyle}
  display: flex;
  flex-direction: column;
  height: calc(100vh - 79px);
  padding-bottom: 12px;
  width: 240px;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    width: 75vw;
    z-index: 3;
  }
`;

export const navLinkStyle = css`
  align-items: center;
  box-sizing: border-box;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  gap: 16px;
  padding: 8px 16px;
  text-decoration: none;
  width: 100%;
`;

export const NavLink = styled(Link)<{ $isActive: boolean; $fill?: boolean }>`
  ${(props) =>
    props.$isActive ? (props.$fill ? navLinkActiveFill : navLinkActive) : ""}

  ${navLinkStyle}

  :hover {
    ${(props) => (props.$fill ? navLinkActiveFill : navLinkActive)}
  }
`;

const NavSubheader = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: ${({ theme }) => theme.fontSizes[2]};
  font-weight: 700;
  padding: 8px 16px;
`;

export { Sidebar };
